<script lang="ts" setup>
import { ref, computed } from 'vue'
import { QInfiniteScroll } from 'quasar'
import { useApi } from '../composables/api'
import type { SubmitBountyResponse } from '@roadfootage/api-client'
import BountyCard from '../components/bounty/BountyCard.vue'
import { isBountyClosed } from '../utils/bounty-status'

const { meApi } = useApi()

const bounties = ref<SubmitBountyResponse[]>([])
const isLoading = ref(false)
const page = ref(0)
const totalCount = ref(0)
const hasMore = computed(() => bounties.value.length < totalCount.value)
const tab = ref<'open' | 'closed'>('open')
const infiniteScrollRef = ref<InstanceType<typeof QInfiniteScroll> | null>(null)

const now = new Date()

const openBounties = computed(() => bounties.value.filter((b) => !isBountyClosed(b, now)))

// Claimed, paid or expired. A claimed bounty belongs here as soon as it is accepted, paid out or not.
const closedBounties = computed(() => bounties.value.filter((b) => isBountyClosed(b, now)))

// The card accepted a response: the server has claimed the bounty, so move it to Closed without a reload.
function onClaimed(bountyId: string) {
  const claimed = bounties.value.find((b) => b.id === bountyId)
  if (!claimed) return
  claimed.isClaimed = true
}

const displayed = computed(() => (tab.value === 'open' ? openBounties.value : closedBounties.value))

async function onLoad(_index: number, done: (stop?: boolean) => void) {
  if (isLoading.value) {
    done(false)
    return
  }
  isLoading.value = true
  try {
    const res = await meApi.mePaginateUserBounties({ page: page.value + 1, pageSize: 12 })
    bounties.value.push(...(res.items ?? []))
    page.value = res.page ?? page.value
    totalCount.value = res.totalCount ?? totalCount.value
  } catch (error) {
    console.error('Failed to load bounties', error)
  } finally {
    isLoading.value = false
    done(!hasMore.value)
  }
}

function loadMore() {
  infiniteScrollRef.value?.trigger()
}
</script>

<template>
  <q-page padding>
    <div class="row q-mb-md items-start q-col-gutter-y-sm">
      <div class="col-12 col-sm">
        <h4 class="text-h4 text-weight-bold q-my-none text-blue-grey-9">Your Bounties</h4>
      </div>
      <div class="col-12 col-sm-auto">
        <q-btn
          unelevated
          color="blue-8"
          icon="mdi-plus"
          label="Post a Bounty"
          :to="{ name: 'submit-bounty' }"
        />
      </div>
    </div>

    <q-tabs v-model="tab" dense align="left" class="q-mb-md" color="blue-8">
      <q-tab name="open" label="Open" :badge="openBounties.length || undefined" />
      <q-tab name="closed" label="Closed" :badge="closedBounties.length || undefined" />
    </q-tabs>

    <q-inner-loading :showing="isLoading && bounties.length === 0" />

    <div v-if="!isLoading && displayed.length === 0" class="text-center text-blue-grey-5 q-pa-xl">
      <q-icon name="mdi-bullhorn-outline" size="4rem" class="q-mb-md" />
      <div class="text-h6">
        {{ tab === 'open' ? 'No open bounties' : 'No closed bounties' }}
      </div>
      <div v-if="tab === 'open'" class="text-body2 q-mt-sm">
        Post a bounty to request footage from contributors.
      </div>
    </div>

    <q-infinite-scroll ref="infiniteScrollRef" @load="onLoad" :offset="250">
      <div class="row q-col-gutter-md">
        <div v-for="bounty in displayed" :key="bounty.id" class="col-12 col-sm-6 col-lg-4">
          <bounty-card :bounty="bounty" @claimed="onClaimed" />
        </div>
      </div>
      <template #loading>
        <div class="row justify-center q-my-md">
          <q-spinner-dots color="primary" size="40px" />
        </div>
      </template>
    </q-infinite-scroll>

    <div v-if="hasMore && !isLoading" class="row justify-center q-my-md">
      <q-btn flat color="primary" label="Load more" icon="mdi-chevron-down" @click="loadMore" />
    </div>
  </q-page>
</template>
